import type { CutPoint } from '../../lib/types';

export type CutStatus = 'detected' | 'adjusted' | 'confirmed';

export interface EditorCut {
  /** Stable local id for React keys / focus tracking. */
  id: string;
  timestampMs: number;
  confidence: number | null;
  status: CutStatus;
}

let seq = 0;
export function nextCutId(): string {
  seq += 1;
  return `cut-${seq}`;
}

/** Build the editor model from the API cut points (§5.5 three-state markers). */
export function cutsFromApi(apiCuts: CutPoint[]): EditorCut[] {
  return [...apiCuts]
    .sort((a, b) => a.timestamp_ms - b.timestamp_ms)
    .map((c) => ({
      id: nextCutId(),
      timestampMs: c.timestamp_ms,
      confidence: c.confidence,
      status: c.user_confirmed ? 'confirmed' : 'detected',
    }));
}

/** Serialize the editor model back to the API replace-all payload (monotonic by timestamp). */
export function cutsToApi(cuts: EditorCut[]): CutPoint[] {
  return [...cuts]
    .sort((a, b) => a.timestampMs - b.timestampMs)
    .map((c, index) => ({
      index_in_job: index,
      timestamp_ms: Math.round(c.timestampMs),
      confidence: c.confidence,
      user_confirmed: c.status === 'confirmed',
    }));
}

/** Move a cut by delta ms, clamped to [0, duration]. Moving marks it "adjusted". */
export function nudgeCut(cut: EditorCut, deltaMs: number, durationMs: number): EditorCut {
  const timestampMs = Math.max(0, Math.min(durationMs, cut.timestampMs + deltaMs));
  if (timestampMs === cut.timestampMs) return cut;
  return { ...cut, timestampMs, status: 'adjusted' };
}

export function confirmCut(cut: EditorCut): EditorCut {
  return { ...cut, status: 'confirmed' };
}

export function countConfirmed(cuts: EditorCut[]): number {
  return cuts.filter((c) => c.status === 'confirmed').length;
}

/**
 * Deterministic pseudo-peaks in [0,1] seeded by a string — a faithful-looking waveform
 * for v0 until the backend exposes a real peaks file. Same seed → same shape (stable render).
 */
export function pseudoPeaks(seed: string, count: number): number[] {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const peaks: number[] = [];
  let state = h >>> 0;
  for (let i = 0; i < count; i++) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    const base = (state / 0xffffffff) * 0.7 + 0.15;
    // Rhythmic envelope so it reads as a beat, not noise.
    const env = 0.55 + 0.45 * Math.abs(Math.sin((i / count) * Math.PI * 8));
    peaks.push(Math.min(1, base * env));
  }
  return peaks;
}
